import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { buildSearchState } from '#/components/search/searchPagination';
import { cn } from '#/utils/cn';
import { APP_NAME } from '#/utils/constants';

import { DesktopNavigation } from './DesktopNavigation';
import { MobileNavigation } from './MobileNavigation';
import {
  applyScrollOffsetVariable,
  CATEGORY_NAVIGATION_SCROLL_OFFSET_VARIABLE,
  MOBILE_PINNED_SCROLL_OFFSET,
  resolveCategoryNavigationScrollOffset,
} from './scrollOffset';
import type { CategoryNavigationProps } from './types';
import { useCategoryNavigationShell } from './useCategoryNavigationShell';

export function CategoryNavigation({
  productTypes,
  sectors = [],
  cta,
  activeHref,
  onMenuClick,
  onSearchClick,
  onSearchSubmit,
  mode = 'responsive',
  defaultExpanded,
  defaultExpandedSections = [],
  pinned,
  title = APP_NAME,
  searchLabel = 'Search market data',
  searchQuery,
  previewSearchState,
  searchPrompt = 'ETFs, stocks, and mutual funds',
  isSearchPending = false,
  enableSearchPreview = false,
  browseEyebrow = 'Browse by',
  browseHeading = 'Investment Product',
  browseDescription = 'Understand categories like ETFs, Mutual Funds, Stocks, etc.',
  className,
}: CategoryNavigationProps) {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const {
    showMobile,
    showDesktop,
    forceDesktopLayout,
    mobileInteractionEnabled,
    desktopInteractionEnabled,
    mobilePinSentinelRef,
    desktopPinSentinelRef,
    desktopPinReleaseOffsetRef,
    desktopPinTransitionLockRef,
    isMobilePinned,
    isPinned,
    desktopBrowseOverlayOpen,
    activeDesktopTab,
    setActiveDesktopTab,
    handleMenuToggle,
    setDesktopBrowseOverlayOpen,
  } = useCategoryNavigationShell({
    activeHref,
    sectors,
    mode,
    pinned,
    onMenuClick,
  });

  const scrollOffset = resolveCategoryNavigationScrollOffset({
    mode,
    showDesktop,
    showMobile,
    desktopInteractionEnabled,
    isDesktopPinned: isPinned,
    isMobilePinned,
  });

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const root = rootRef.current;

    if (!root) {
      return;
    }

    return applyScrollOffsetVariable({
      root,
      doc: document,
      variableName: CATEGORY_NAVIGATION_SCROLL_OFFSET_VARIABLE,
      value: scrollOffset,
    });
  }, [scrollOffset]);

  const handleSearchSubmit =
    onSearchSubmit ??
    ((query: string) => {
      void navigate({
        to: '/search',
        search: buildSearchState({
          query,
          investment_type: undefined,
        }),
      });
    });

  return (
    <>
      {showDesktop ? (
        <div
          ref={desktopPinSentinelRef}
          aria-hidden="true"
          data-category-navigation-sentinel="true"
          className={cn(
            'pointer-events-none h-0',
            mode === 'responsive' ? 'hidden lg:block' : undefined,
          )}
        />
      ) : null}

      <div
        ref={rootRef}
        data-category-navigation-root="true"
        className={cn(
          'bg-gray-10 relative flex flex-col gap-16',
          showMobile || showDesktop ? 'sticky top-0 z-40' : undefined,
          desktopInteractionEnabled ? '[overflow-anchor:none]' : undefined,
          className,
        )}
      >
        {showMobile ? (
          <>
            <div
              ref={mobilePinSentinelRef}
              aria-hidden="true"
              data-category-navigation-mobile-sentinel="true"
              className={cn(
                'pointer-events-none absolute top-0 right-0 left-0 h-0',
                mode === 'responsive' ? 'lg:hidden' : undefined,
              )}
            />
            <div
              className={cn(mode === 'responsive' ? 'lg:hidden' : undefined)}
              style={
                isMobilePinned
                  ? { minHeight: MOBILE_PINNED_SCROLL_OFFSET }
                  : undefined
              }
            >
              <div
                data-pinned={isMobilePinned ? 'true' : 'false'}
                className={cn(
                  'flow flow-12 relative',
                  isMobilePinned
                    ? 'fixed inset-x-0 top-0 z-40 drop-shadow-[0_8px_16px_rgba(0,52,83,0.06)]'
                    : undefined,
                )}
              >
                <MobileNavigation
                  title={title}
                  searchLabel={searchLabel}
                  searchQuery={searchQuery}
                  previewSearchState={previewSearchState}
                  enableSearchPreview={enableSearchPreview}
                  searchPrompt={searchPrompt}
                  productTypes={productTypes}
                  sectors={sectors}
                  cta={cta}
                  browseEyebrow={browseEyebrow}
                  activeHref={activeHref}
                  expandedByDefault={
                    defaultExpanded ?? defaultExpandedSections.length > 0
                  }
                  productTypesExpandedByDefault={defaultExpandedSections.includes(
                    'productTypes',
                  )}
                  sectorsExpandedByDefault={defaultExpandedSections.includes(
                    'sectors',
                  )}
                  onSearchSubmit={handleSearchSubmit}
                  isPinned={isMobilePinned}
                  isInteractive={mobileInteractionEnabled}
                  isSearchPending={isSearchPending}
                  onSearchClick={onSearchClick}
                />
              </div>
            </div>
          </>
        ) : null}

        {showDesktop ? (
          <>
            <div
              className={cn(
                isPinned || desktopBrowseOverlayOpen
                  ? 'drop-shadow-[0_8px_16px_rgba(0,52,83,0.06)]'
                  : undefined,
                mode === 'responsive' ? 'hidden lg:block' : undefined,
              )}
            >
              <DesktopNavigation
                title={title}
                searchLabel={searchLabel}
                searchQuery={searchQuery}
                previewSearchState={previewSearchState}
                enableSearchPreview={enableSearchPreview}
                searchPrompt={searchPrompt}
                browseEyebrow={browseEyebrow}
                browseHeading={browseHeading}
                browseDescription={browseDescription}
                productTypes={productTypes}
                sectors={sectors}
                cta={cta}
                activeHref={activeHref}
                activeTab={activeDesktopTab}
                isPinned={isPinned}
                pinReleaseOffsetRef={desktopPinReleaseOffsetRef}
                pinTransitionLockRef={desktopPinTransitionLockRef}
                overlayOpen={desktopBrowseOverlayOpen}
                isInteractive={desktopInteractionEnabled}
                isSearchPending={isSearchPending}
                forceDesktopLayout={forceDesktopLayout}
                onTabChange={setActiveDesktopTab}
                onMenuToggle={handleMenuToggle}
                onOverlayOpenChange={setDesktopBrowseOverlayOpen}
                onSearchSubmit={handleSearchSubmit}
                onSearchClick={onSearchClick}
              />
            </div>
          </>
        ) : null}
      </div>
    </>
  );
}
